import React, { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, AlertTriangle, Camera, RotateCw, Loader2, LogIn, IdCard } from "lucide-react";
import { Link } from "react-router-dom";

const COOLDOWN_MS = 1800;
const SOUND = (ok) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = ok ? 880 : 220;
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
    o.start(); o.stop(ctx.currentTime + 0.22);
  } catch (e) {}
};

export default function Scanner() {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const streamRef = useRef(null);
  const lastScanRef = useRef({ text: "", at: 0 });
  const [cameras, setCameras] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [status, setStatus] = useState("idle"); // idle | starting | scanning | error
  const [lastResult, setLastResult] = useState(null);
  const [lastRaw, setLastRaw] = useState("");
  const [stats, setStats] = useState({ ok: 0, dup: 0, miss: 0 });
  const [permError, setPermError] = useState("");

  useEffect(() => {
    return () => stopInternal();
  }, []);

  const stopInternal = () => {
    try { readerRef.current?.reset?.(); } catch (e) {}
    try { streamRef.current?.getTracks()?.forEach((t) => t.stop()); } catch (e) {}
    streamRef.current = null;
  };

  if (user === false) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center p-6 text-center">
        <IdCard size={56} className="text-blue-400 mb-4" />
        <h1 className="font-display text-2xl font-bold mb-2">Lector de credenciales</h1>
        <p className="text-slate-400 mb-6">Para usar el scanner debes iniciar sesión en este dispositivo.</p>
        <Link to={`/login?next=${encodeURIComponent("/scanner")}`}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-3 rounded-lg">
          <LogIn size={18} /> Iniciar sesión
        </Link>
      </div>
    );
  }

  const start = async (overrideDeviceId) => {
    setStatus("starting");
    setPermError("");
    stopInternal();

    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus("error");
      setPermError("Este navegador no soporta cámara. Usa Chrome o Safari.");
      return;
    }
    if (!window.isSecureContext) {
      setStatus("error");
      setPermError("La cámara requiere HTTPS.");
      return;
    }

    const chosen = overrideDeviceId ?? deviceId;
    const constraints = chosen
      ? { video: { deviceId: { exact: chosen } }, audio: false }
      : { video: { facingMode: { ideal: "environment" } }, audio: false };

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (e) {
      // Fallback: si falló con deviceId exacto, reintentar con facingMode
      if (chosen) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
        } catch (e2) { return handleCamError(e2); }
      } else {
        return handleCamError(e);
      }
    }

    streamRef.current = stream;
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = stream;
    video.setAttribute("playsinline", "true");
    video.setAttribute("autoplay", "true");
    video.muted = true;
    try { await video.play(); } catch (e) {}

    // Enumerar devices ahora que tenemos permiso (labels disponibles)
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      const vids = devs.filter((d) => d.kind === "videoinput");
      setCameras(vids);
      if (!deviceId) {
        const back = vids.find((d) => /back|rear|environment|trás/i.test(d.label));
        setDeviceId((back || vids[0])?.deviceId || "");
      }
    } catch (e) {}

    // Montar decoder sobre el video ya reproduciéndose
    try {
      const hints = new Map();
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.PDF_417, BarcodeFormat.QR_CODE, BarcodeFormat.CODE_128,
        BarcodeFormat.CODE_39, BarcodeFormat.EAN_13, BarcodeFormat.DATA_MATRIX,
      ]);
      hints.set(DecodeHintType.TRY_HARDER, true);
      const reader = new BrowserMultiFormatReader(hints, 200);
      readerRef.current = reader;
      reader.decodeFromVideoElement(video, (result, err) => {
        if (result) handleHit(result.getText());
      });
      setStatus("scanning");
    } catch (e) {
      setStatus("error");
      setPermError("No se pudo iniciar el decoder: " + (e?.message || e));
    }
  };

  const handleCamError = (e) => {
    setStatus("error");
    const name = e?.name || "";
    if (name === "NotAllowedError" || name === "PermissionDeniedError") {
      setPermError("Permiso de cámara denegado. Habilítalo en los ajustes del navegador y recarga.");
    } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
      setPermError("No se encontró ninguna cámara en el dispositivo.");
    } else if (name === "NotReadableError" || name === "TrackStartError") {
      setPermError("La cámara está siendo usada por otra app. Ciérrala y reintenta.");
    } else if (name === "OverconstrainedError") {
      setPermError("La cámara seleccionada no está disponible. Prueba otra.");
    } else {
      setPermError(e?.message || "No se pudo acceder a la cámara");
    }
  };

  const stop = () => { stopInternal(); setStatus("idle"); };

  const handleHit = async (text) => {
    const now = Date.now();
    if (lastScanRef.current.text === text && now - lastScanRef.current.at < COOLDOWN_MS) return;
    lastScanRef.current = { text, at: now };
    setLastRaw(text);
    try {
      const { data } = await api.post("/workers/scan-credencial", { raw: text });
      if (data.found && !data.already_marked) {
        setStats((s) => ({ ...s, ok: s.ok + 1 }));
        setLastResult({ ok: true, dup: false, data });
        SOUND(true);
        toast.success(`✓ ${data.worker.first_name} ${data.worker.last_name} · credencial entregada`);
      } else if (data.found && data.already_marked) {
        setStats((s) => ({ ...s, dup: s.dup + 1 }));
        setLastResult({ ok: true, dup: true, data });
        SOUND(true);
        toast.info(`⟳ ${data.worker.first_name} ${data.worker.last_name} · ya tenía credencial`);
      } else {
        setStats((s) => ({ ...s, miss: s.miss + 1 }));
        setLastResult({ ok: false, data: { rut: data.rut, msg: "No existe en la base" } });
        SOUND(false);
        toast.error(`✗ RUT ${data.rut} · no está en la base`);
      }
    } catch (e) {
      setStats((s) => ({ ...s, miss: s.miss + 1 }));
      setLastResult({ ok: false, data: { msg: e.response?.data?.detail || "Error al escanear" } });
      SOUND(false);
    }
  };

  const onSelectCamera = (e) => {
    const id = e.target.value;
    setDeviceId(id);
    if (status === "scanning") start(id);
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <div className="p-4 flex items-center justify-between border-b border-slate-800">
        <div className="flex items-center gap-2">
          <IdCard size={22} className="text-blue-400" />
          <div>
            <h1 className="font-display font-bold text-lg">Lector de credenciales</h1>
            <p className="text-[11px] text-slate-400">{user?.email}</p>
          </div>
        </div>
        <div className="text-xs text-slate-400 flex items-center gap-3">
          <span className="text-emerald-400 font-semibold">✓ {stats.ok}</span>
          <span className="text-blue-300 font-semibold">⟳ {stats.dup}</span>
          <span className="text-red-400 font-semibold">✗ {stats.miss}</span>
        </div>
      </div>

      <div className="relative bg-black">
        <video ref={videoRef} data-testid="scanner-video" playsInline autoPlay muted className="w-full aspect-[3/4] object-cover" />
        {status !== "scanning" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/70 px-6 text-center">
            {status === "starting" ? <Loader2 className="animate-spin text-blue-400" size={32} /> : <Camera size={44} className="text-slate-500" />}
            <button data-testid="scanner-start" onClick={() => start()} disabled={status === "starting"}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-full shadow-lg disabled:opacity-60">
              <Camera size={18} /> {status === "idle" ? "Iniciar cámara" : status === "starting" ? "Iniciando…" : "Reintentar"}
            </button>
            {permError && <p className="text-sm text-red-300">{permError}</p>}
            {status === "error" && <p className="text-[11px] text-slate-400">Si denegaste el permiso, ve a los ajustes del sitio → Permisos → Cámara y actívala.</p>}
          </div>
        )}
        {status === "scanning" && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute left-6 right-6 top-1/2 -translate-y-1/2 h-32 border-2 border-blue-400/70 rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]"></div>
          </div>
        )}
      </div>

      <div className="p-4 space-y-3">
        {cameras.length > 1 && (
          <select data-testid="scanner-camera-select" value={deviceId} onChange={onSelectCamera}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm">
            {cameras.map((c, i) => <option key={c.deviceId} value={c.deviceId}>{c.label || `Cámara ${i + 1}`}</option>)}
          </select>
        )}
        {status === "scanning" && (
          <button onClick={stop} data-testid="scanner-stop" className="w-full inline-flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-semibold py-2.5 rounded-lg">
            <RotateCw size={15} /> Detener
          </button>
        )}
        {lastResult && (
          <div data-testid="scanner-last-result" className={`rounded-xl p-4 border ${lastResult.ok ? (lastResult.dup ? "bg-blue-900/40 border-blue-700/60" : "bg-emerald-900/40 border-emerald-700/60") : "bg-red-900/40 border-red-700/60"}`}>
            <div className="flex items-start gap-3">
              {lastResult.ok ? <CheckCircle2 className={lastResult.dup ? "text-blue-300" : "text-emerald-300"} size={22} /> : <AlertTriangle className="text-red-300" size={22} />}
              <div className="flex-1 min-w-0">
                {lastResult.ok ? (
                  <>
                    <p className="font-semibold">{lastResult.data.worker.first_name} {lastResult.data.worker.last_name}</p>
                    <p className="text-xs text-slate-300">{lastResult.data.rut} · {lastResult.data.worker.position || "—"}</p>
                    <p className="text-xs text-slate-400">{lastResult.data.worker.mandante || "—"}{lastResult.data.worker.contrato ? ` · ${lastResult.data.worker.contrato}` : ""}</p>
                    <p className={`text-xs mt-1 font-semibold ${lastResult.dup ? "text-blue-300" : "text-emerald-300"}`}>{lastResult.dup ? "Ya tenía credencial" : "Credencial entregada ✓"}</p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold">No encontrado</p>
                    <p className="text-xs text-slate-300">{lastResult.data.rut || "—"}</p>
                    <p className="text-xs text-red-300 mt-1">{lastResult.data.msg}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        {lastRaw && (
          <details className="bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2">
            <summary className="text-[11px] text-slate-400 cursor-pointer">Último payload leído</summary>
            <code data-testid="scanner-last-raw" className="block mt-2 text-[11px] text-slate-300 break-all whitespace-pre-wrap">{lastRaw}</code>
          </details>
        )}
        <p className="text-[11px] text-slate-500 text-center pt-2">Apunta al código PDF417 (reverso de la cédula) o al QR de la credencial.</p>
      </div>
    </div>
  );
}
